import { CLI_MODELS, DEFAULT_EFFORT_LEVEL } from '@rudi/shared/chat/model-options';
import type { ChatModelOption } from '../api/types';

export function effortLabel(effort: string): string {
  if (effort === 'xhigh') return 'Extra high';
  if (effort === 'max') return 'Maximum';
  return effort.charAt(0).toUpperCase() + effort.slice(1);
}

export function ChatModelFields({
  label = 'Chat',
  model,
  effort,
  options,
  disabled,
  onChange,
  unlistedNote = 'current',
}: {
  /** Names the fields for assistive technology when a page shows several. */
  label?: string;
  /** Follows a saved model the options no longer list, such as one OpenRouter retired. */
  unlistedNote?: string;
  model: string;
  effort: string;
  options: ChatModelOption[];
  disabled: boolean;
  onChange: (model: string, effort: string) => void;
}) {
  const selected = options.find(option => option.model === model);
  const providerDefault = /^(deepinfra|infercom)\//.test(model);
  const choices = [
    ...CLI_MODELS.filter(item => options.some(option => option.model === item.value)).map(item => ({
      model: item.value,
      label: item.label,
      efforts: options.find(option => option.model === item.value)?.efforts ?? [],
    })),
    ...options.filter(option => !CLI_MODELS.some(item => item.value === option.model)),
  ];
  const listed = choices.some(option => option.model === model);

  return (
    <>
      <label className="block space-y-1 text-sm">
        <span>Model</span>
        <select
          aria-label={`${label} model`}
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          value={model}
          disabled={disabled || !options.some(option => option.efforts.length > 0)}
          onChange={event => {
            const option = options.find(item => item.model === event.target.value);
            if (!option?.efforts.length) return;
            const nextEffort = option.efforts.includes(effort)
              ? effort
              : option.efforts.includes(DEFAULT_EFFORT_LEVEL)
                ? DEFAULT_EFFORT_LEVEL
                : option.efforts[0];
            onChange(option.model, nextEffort);
          }}
        >
          {!listed && (
            <option value={model} disabled>
              {model} · {unlistedNote}
            </option>
          )}
          {choices.map(option => (
            <option key={option.model} value={option.model} disabled={!option.efforts.length}>
              {option.label}
              {!option.efforts.length ? ' · unavailable' : ''}
            </option>
          ))}
        </select>
      </label>
      <label className="block space-y-1 text-sm">
        <span>Reasoning level</span>
        <select
          aria-label={`${label} reasoning level`}
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          value={effort}
          disabled={disabled || !selected?.efforts.length}
          onChange={event => {
            if (selected?.efforts.includes(event.target.value)) onChange(model, event.target.value);
          }}
        >
          {!selected?.efforts.includes(effort) && (
            <option value={effort} disabled>
              {effortLabel(effort)} · current
            </option>
          )}
          {selected?.efforts.map(value => (
            <option key={value} value={value}>
              {providerDefault ? 'Provider default' : effortLabel(value)}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-muted-foreground">
        Available models and reasoning levels depend on this Rudi connection and executor.
      </p>
    </>
  );
}
